// src/components/Alterar.js
// Tela de ALTERAÇÃO — busca produto por ID e permite editar seus dados

import React, { useState } from "react";
import { getProdutoById, updateProduto } from "../services/api";

export default function Alterar({ onVoltar }) {
  // ── Estados ──────────────────────────────────────────────────────
  const [idBusca, setIdBusca]   = useState("");     // ID digitado para buscar
  const [produto, setProduto]   = useState(null);   // produto carregado
  const [form, setForm]         = useState({});     // dados editáveis
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [mensagem, setMensagem] = useState(null);   // { tipo, texto }
  const [erros, setErros]       = useState({});

  // ── Passo 1: Busca o produto pelo ID ────────────────────────────
  async function buscarProduto() {
    if (!idBusca || isNaN(idBusca)) {
      setMensagem({ tipo: "erro", texto: "Digite um ID numérico válido." });
      return;
    }
    try {
      setLoadingBusca(true);
      setMensagem(null);
      setProduto(null);

      const data = await getProdutoById(idBusca);
      setProduto(data);
      // Pré-popula o formulário com os dados atuais
      setForm({ nome: data.nome, preco: data.preco, estoque: data.estoque });
    } catch (e) {
      setMensagem({ tipo: "erro", texto: `Produto com ID ${idBusca} não encontrado.` });
    } finally {
      setLoadingBusca(false);
    }
  }

  // ── Handler de mudança nos campos ────────────────────────────────
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (erros[name]) setErros((prev) => ({ ...prev, [name]: "" }));
  }

  // ── Validação ────────────────────────────────────────────────────
  function validar() {
    const novosErros = {};
    if (!form.nome?.trim()) novosErros.nome = "Nome é obrigatório";
    if (!form.preco || isNaN(form.preco) || Number(form.preco) <= 0)
      novosErros.preco = "Preço deve ser um número positivo";
    if (!form.estoque?.toString() || isNaN(form.estoque) || Number(form.estoque) < 0)
      novosErros.estoque = "Estoque deve ser um número inteiro ≥ 0";
    return novosErros;
  }

  // ── Passo 2: Salva as alterações ─────────────────────────────────
  async function handleSalvar(e) {
    e.preventDefault();
    const errosValidacao = validar();
    if (Object.keys(errosValidacao).length > 0) {
      setErros(errosValidacao);
      return;
    }
    try {
      setLoadingSalvar(true);
      setMensagem(null);

      const produtoAtualizado = {
        ...produto,                          // mantém o id e outros campos
        nome:    form.nome.trim(),
        preco:   parseFloat(form.preco),
        estoque: parseInt(form.estoque, 10),
      };

      await updateProduto(produto.id, produtoAtualizado);
      setMensagem({ tipo: "sucesso", texto: `Produto #${produto.id} atualizado com sucesso!` });
      setProduto(produtoAtualizado); // atualiza o estado local
    } catch (e) {
      setMensagem({ tipo: "erro", texto: "Erro ao salvar alterações." });
    } finally {
      setLoadingSalvar(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="tela">
      <div className="tela-header">
        <h2>✏️ Alterar Produto</h2>
        <p>Busque um produto pelo ID e edite seus dados</p>
      </div>

      {/* PASSO 1: Busca por ID */}
      <div className="busca-id-container">
        <label>Informe o ID do produto:</label>
        <div className="busca-id-row">
          <input
            type="number"
            min="1"
            value={idBusca}
            onChange={(e) => setIdBusca(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && buscarProduto()}
            placeholder="Ex: 1"
            className="input-id"
          />
          <button
            onClick={buscarProduto}
            className="btn btn-secondary"
            disabled={loadingBusca}
          >
            {loadingBusca ? "⏳ Buscando..." : "🔍 Buscar"}
          </button>
        </div>
      </div>

      {/* Mensagens de feedback */}
      {mensagem && (
        <div className={`status-msg ${mensagem.tipo}`}>
          {mensagem.tipo === "sucesso" ? "✅" : "❌"} {mensagem.texto}
          <button className="btn-fechar-msg" onClick={() => setMensagem(null)}>✕</button>
        </div>
      )}

      {/* PASSO 2: Formulário de edição (exibido após encontrar o produto) */}
      {produto && (
        <form onSubmit={handleSalvar} className="formulario" noValidate>
          <div className="produto-id-badge">
            Editando produto <strong>#{produto.id}</strong>
          </div>

          <div className="campo">
            <label htmlFor="alt-nome">Nome do Produto *</label>
            <input
              id="alt-nome"
              name="nome"
              type="text"
              value={form.nome}
              onChange={handleChange}
              className={erros.nome ? "input-erro" : ""}
            />
            {erros.nome && <span className="msg-erro">{erros.nome}</span>}
          </div>

          <div className="campo">
            <label htmlFor="alt-preco">Preço (R$) *</label>
            <input
              id="alt-preco"
              name="preco"
              type="number"
              step="0.01"
              min="0"
              value={form.preco}
              onChange={handleChange}
              className={erros.preco ? "input-erro" : ""}
            />
            {erros.preco && <span className="msg-erro">{erros.preco}</span>}
          </div>

          <div className="campo">
            <label htmlFor="alt-estoque">Estoque (unidades) *</label>
            <input
              id="alt-estoque"
              name="estoque"
              type="number"
              min="0"
              step="1"
              value={form.estoque}
              onChange={handleChange}
              className={erros.estoque ? "input-erro" : ""}
            />
            {erros.estoque && <span className="msg-erro">{erros.estoque}</span>}
          </div>

          <div className="form-acoes">
            <button type="submit" className="btn btn-primary" disabled={loadingSalvar}>
              {loadingSalvar ? "⏳ Salvando..." : "💾 Salvar Alterações"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setForm({ nome: produto.nome, preco: produto.preco, estoque: produto.estoque })}
            >
              ↩ Desfazer
            </button>       
          </div>
        </form>
      )}
    </div>
  );
}
